import { useState } from "react";
import { Music, Share2, Copy, Check, BadgeCheck } from "lucide-react";
import { FaSpotify, FaApple, FaYoutube, FaItunes, FaNapster } from "react-icons/fa";
import { SiTidal, SiAudiomack } from "react-icons/si";
import { QRCodeSVG } from "qrcode.react";
import { useLanguage } from "@/contexts/LanguageContext";
import MuslinkLogo from "@/components/MuslinkLogo";

// Custom SVG icon components using uploaded SVG files
const SoundCloudIcon = (props) => (
  <img src="/icons/sc-mus.svg" alt="SoundCloud" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const TikTokIcon = (props) => (
  <img src="/icons/tik-tok.svg" alt="TikTok" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const VKMusicIcon = (props) => (
  <img src="/icons/vk-mus.svg" alt="VK Музыка" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const YandexMusicIcon = (props) => (
  <img src="/icons/yandex-mus.svg" alt="Яндекс Музыка" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const CustomLinkIcon = (props) => (
  <img src="/icons/link.svg" alt="Ссылка" className={props.className} style={{ width: '24px', height: '24px', ...props.style }} />
);

const YouTubeMusicIcon = (props) => (
  <img src="/icons/youtube-music.svg" alt="YouTube Music" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const AmazonMusicIcon = (props) => (
  <img src="/icons/amazon-mus.svg" alt="Amazon Music" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const AmazonStoreIcon = (props) => (
  <img src="/icons/amazon-store.svg" alt="Amazon Store" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const ZvukIcon = (props) => (
  <img src="/icons/zvuk-mus.svg" alt="Звук" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const AnghamiIcon = (props) => (
  <img src="/icons/anghami-mus.png" alt="Anghami" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const AudiusIcon = (props) => (
  <img src="/icons/audius-mus.png" alt="Audius" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const BandcampIcon = (props) => (
  <img src="/icons/bandcamp-mus.png" alt="Bandcamp" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const MtsIcon = (props) => (
  <img src="/icons/mts-mus.png" alt="MTS Music" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const DeezerIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M6.01 11.75H0V15h6.01v-3.25zM6.01 7.25H0v3.25h6.01V7.25zM6.01 16.25H0v3.25h6.01v-3.25zM12.005 11.75H6.01V15h5.995v-3.25zM12.005 16.25H6.01v3.25h5.995v-3.25zM17.995 11.75H12V15h5.995v-3.25zM17.995 16.25H12v3.25h5.995v-3.25zM17.995 7.25H12v3.25h5.995V7.25zM24 11.75h-6.005V15H24v-3.25zM24 16.25h-6.005v3.25H24v-3.25zM24 7.25h-6.005v3.25H24V7.25zM24 2.75h-6.005V6H24V2.75z"/>
  </svg>
);

const PandoraIcon = (props) => (
  <img src="/icons/pandora-mus.png" alt="Pandora" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

const BoomplayIcon = (props) => (
  <img src="/icons/boomplay-mus.png" alt="Boomplay" className={props.className} style={{ width: '28px', height: '28px', ...props.style }} />
);

// Platform definitions - exact match with PublicPage.jsx
const PLATFORMS = {
  spotify: { icon: FaSpotify, color: "#1DB954", bgClass: "platform-spotify", isImage: false },
  appleMusic: { icon: FaApple, color: "#FA233B", bgClass: "platform-apple", isImage: false },
  itunes: { icon: FaItunes, color: "#EA4CC0", bgClass: "platform-itunes", isImage: false },
  youtube: { icon: FaYoutube, color: "#FF0000", bgClass: "platform-youtube", isImage: false },
  youtubeMusic: { icon: YouTubeMusicIcon, color: "#FF0000", bgClass: "platform-youtube", isImage: true },
  yandex: { icon: YandexMusicIcon, color: "#000000", bgClass: "platform-yandex", isImage: true },
  vk: { icon: VKMusicIcon, color: "#000000", bgClass: "platform-vk", isImage: true },
  tiktok: { icon: TikTokIcon, color: "#000000", bgClass: "platform-tiktok", isImage: true },
  deezer: { icon: DeezerIcon, color: "#A238FF", bgClass: "platform-deezer", isImage: false },
  tidal: { icon: SiTidal, color: "#000000", bgClass: "platform-tidal", isImage: false },
  amazonMusic: { icon: AmazonMusicIcon, color: "#000000", bgClass: "platform-amazon", isImage: true },
  amazonStore: { icon: AmazonStoreIcon, color: "#FF9900", bgClass: "platform-amazon-store", isImage: true },
  soundcloud: { icon: SoundCloudIcon, color: "#000000", bgClass: "platform-soundcloud", isImage: true },
  pandora: { icon: PandoraIcon, color: "#000000", bgClass: "platform-pandora", isImage: true },
  napster: { icon: FaNapster, color: "#000000", bgClass: "platform-napster", isImage: false },
  audiomack: { icon: SiAudiomack, color: "#FFA200", bgClass: "platform-audiomack", isImage: false },
  audius: { icon: AudiusIcon, color: "#000000", bgClass: "platform-audius", isImage: true },
  anghami: { icon: AnghamiIcon, color: "#000000", bgClass: "platform-anghami", isImage: true },
  boomplay: { icon: BoomplayIcon, color: "#000000", bgClass: "platform-boomplay", isImage: true },
  bandcamp: { icon: BandcampIcon, color: "#000000", bgClass: "platform-bandcamp", isImage: true },
  zvuk: { icon: ZvukIcon, color: "#FFFFFF", bgClass: "platform-zvuk", isImage: true },
  mts: { icon: MtsIcon, color: "#000000", bgClass: "platform-mts", isImage: true },
  apple: { icon: FaApple, color: "#FA233B", bgClass: "platform-apple", isImage: false },
  amazon: { icon: AmazonMusicIcon, color: "#000000", bgClass: "platform-amazon", isImage: true },
  custom: { icon: CustomLinkIcon, color: "#000000", bgClass: "", isImage: true },
};

/**
 * PagePreview component - renders a live preview of the public page
 * Exact replica of PublicPage.jsx styles
 */
export default function PagePreview({ formData, links, qrEnabled, shareButtonsEnabled = true, publicUrl, isVerified, compact = false, className = "" }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const getPlatformInfo = (platformId) => {
    const platform = PLATFORMS[platformId] || PLATFORMS.custom;
    return {
      ...platform,
      name: t('platforms', platformId) || platformId
    };
  };

  const getCoverUrl = (url) => {
    if (!url) return null;
    return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  const handleCopy = () => {
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const coverUrl = getCoverUrl(formData.cover_image);
  const isLightTheme = formData.page_theme === "light";
  const activeLinks = links.filter(l => l.active);

  return (
    <div 
      className={`${compact ? 'h-fit justify-start px-4 pt-4 pb-[5px]' : 'min-h-full justify-start p-3 sm:p-4'} flex flex-col items-center relative overflow-hidden ${
        isLightTheme ? 'bg-gray-50' : 'bg-zinc-950'
      } ${className}`}
    >
      {/* Blurred Background */}
      {coverUrl && (
        <div 
          className="absolute inset-0 bg-cover bg-center pointer-events-none"
          style={{
            backgroundImage: `url(${coverUrl})`,
            filter: 'blur(55px)',
            transform: 'scale(1.2)',
            opacity: isLightTheme ? 0.15 : 0.3,
          }}
        />
      )}
      
      {/* Gradient Overlay */}
      <div className={`absolute inset-0 pointer-events-none ${
        isLightTheme 
          ? 'bg-gradient-to-b from-transparent via-gray-50/50 to-gray-50' 
          : 'bg-gradient-to-b from-transparent via-zinc-950/50 to-zinc-950'
      }`} />
      
      {/* Content - removed motion for smoother scrolling */}
      <div className={`relative z-10 w-full ${compact ? 'max-w-sm py-0' : 'max-w-md py-4'}`}>
        {/* Glass Card */}
        <div className={`rounded-2xl sm:rounded-3xl ${compact ? 'p-4' : 'p-5 sm:p-8'} text-center ${
          isLightTheme 
            ? 'bg-white/80 backdrop-blur-xl border border-gray-200 shadow-xl' 
            : 'glass'
        }`}>
          {/* Cover Image */}
          <div 
            className={`${compact ? 'w-28 h-28 mb-4' : 'w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40 mb-4 sm:mb-6'} rounded-xl sm:rounded-2xl mx-auto overflow-hidden shadow-2xl ${
              isLightTheme ? '' : 'neon-glow'
            }`}
          >
            {coverUrl ? (
              <img 
                src={coverUrl}
                alt={formData.title || "Cover"}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className={`w-full h-full ${isLightTheme ? 'bg-gray-100' : 'bg-zinc-800'} flex items-center justify-center`}>
                <Music className={`w-12 h-12 sm:w-16 sm:h-16 ${isLightTheme ? 'text-gray-400' : 'text-muted-foreground'}`} />
              </div>
            )}
          </div>
          
          {/* Artist Info */}
          <div>
            <h1 className={`font-display ${compact ? 'text-xl' : 'text-xl sm:text-2xl md:text-3xl'} uppercase tracking-tight mb-1 text-center ${
              isLightTheme ? 'text-gray-900' : 'text-white'
            }`}>
              {formData.artist_name || t('pageBuilder', 'artistName')}
              {isVerified && (
                <BadgeCheck
                  className={`inline-block w-5 h-5 sm:w-6 sm:h-6 ml-1 sm:ml-2 ${isLightTheme ? 'text-gray-900' : 'text-white'}`}
                  style={{ verticalAlign: "middle", transform: "translateY(-0.12em)" }}
                />
              )}
            </h1>
            <p className={`${compact ? 'text-sm' : 'text-base sm:text-lg'} ${isLightTheme ? 'text-gray-600' : 'text-zinc-400'} mb-1 sm:mb-2`}>
              {formData.release_title || t('pageBuilder', 'releaseTitle')}
            </p>
            {formData.description && (
              <p className={`text-xs sm:text-sm ${isLightTheme ? 'text-gray-500' : 'text-zinc-500'} mb-4 sm:mb-6 max-w-xs mx-auto`}>
                {formData.description}
              </p>
            )}
          </div>
          
          {/* Links - exact style from PublicPage */}
          <div className={`${compact ? 'space-y-2 mt-5' : 'space-y-2 sm:space-y-3 mt-6 sm:mt-8'}`}>
            {activeLinks.length === 0 ? (
              <p className={`${isLightTheme ? 'text-gray-500' : 'text-muted-foreground'} py-8`}>
                {t('pageBuilder', 'linksWillAppear')}
              </p>
            ) : (
              activeLinks.map((link, i) => {
                const platform = getPlatformInfo(link.platform);
                const Icon = platform.icon;
                const isImageIcon = platform.isImage;
                
                return (
                  <button
                    key={link.id}
                    className={`w-full flex items-center justify-between ${
                      isLightTheme 
                        ? 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900' 
                        : 'bg-zinc-900/80 hover:bg-zinc-800 border-white/10 text-white'
                    } border hover:border-primary/50 p-3 sm:p-4 rounded-lg sm:rounded-xl transition-all group link-hover`}
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      {isImageIcon ? (
                        /* For SVG image icons - show the icon directly with proper size */
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110 overflow-hidden"
                          style={{ backgroundColor: platform.color }}
                        >
                          <Icon style={{ width: '28px', height: '28px' }} />
                        </div>
                      ) : (
                        /* For react-icons - show with colored background */
                        <div 
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110"
                          style={{ backgroundColor: platform.color }}
                        >
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                      )}
                      <span className="font-medium text-sm sm:text-base">
                        {link.custom_name || platform.name}
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {t('common', 'listen')}
                    </span>
                  </button>
                );
              })
            )}
          </div>
          
          {/* Share Buttons - exact style from PublicPage */}
          {shareButtonsEnabled && (
          <div className="flex gap-2 sm:gap-3 mt-5 sm:mt-6 justify-center">
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 ${
                isLightTheme 
                  ? 'bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-700' 
                  : 'bg-white/10 hover:bg-white/20 border-white/20 text-white'
              } border rounded-lg text-xs sm:text-sm transition-all`}
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
                  <span className="text-green-500">{t('publicPage', 'linkCopied')}</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>{t('publicPage', 'copy')}</span>
                </>
              )}
            </button>
            
            <button
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-primary/20 hover:bg-primary/30 border border-primary/30 rounded-lg text-xs sm:text-sm transition-all ${
                isLightTheme ? 'text-primary' : 'text-white'
              }`}
            >
              <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
              <span className={isLightTheme ? 'text-primary' : 'text-white'}>{t('publicPage', 'share')}</span>
            </button>
          </div>
          )}
          
          {/* QR Code - exact style from PublicPage */}
          {qrEnabled && publicUrl && (
            <div className={`${compact ? 'mt-5' : 'mt-6 sm:mt-8'} flex flex-col items-center`}>
              <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl shadow-lg cursor-pointer hover:scale-105 transition-transform ${
                isLightTheme ? 'bg-gray-50 border border-gray-200' : 'bg-white'
              }`}>
                <QRCodeSVG
                  value={publicUrl}
                  size={compact ? 72 : 80}
                  level="M"
                  includeMargin={false}
                  bgColor={isLightTheme ? "#fafafa" : "#ffffff"}
                  fgColor="#18181b"
                />
              </div>
              <p className={`text-[10px] sm:text-xs mt-2 ${isLightTheme ? 'text-gray-500' : 'text-zinc-500'}`}>
                {t('publicPage', 'scanToShare')}
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className={`${compact ? 'mt-3' : 'mt-6'} flex justify-center`}>
          <a href="https://mus.link/" target="_blank" rel="noopener noreferrer" aria-label="Mus.Link">
            <MuslinkLogo height={compact ? 12 : 14} theme={isLightTheme ? "light" : "dark"} className={isLightTheme ? "opacity-45 hover:opacity-70 transition-opacity" : "opacity-30 hover:opacity-60 transition-opacity"} />
          </a>
        </div>
      </div>
    </div>
  );
}
